// Mobilne menu wariantu Wise UI.
//
// W stosunku do legacy MenuPopup:
// - dodaje wybór języka (przeniesione z WiseHeaderTopBar)
// - dodaje przycisk Wyloguj (przeniesione z WiseHeaderTopBar)

import React, { ReactElement } from 'react';
import classnames from 'classnames';
import { X, BoxArrowRight } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import { reduxActions, useDispatch, useSelector } from 'store';
import { useAppNavigate } from 'hooks';
import { useGetLayoutLanguages, useGetLayoutUrlResolverToLanguage, usePostOverLogout } from 'api';
import WiseDashboardMenu from 'components/wise/WiseDashboardMenu';
import {
  userRolesAccessRoutes,
  filterAccessRoutesByPermissions
} from 'components/layouts/DashboardSubLayout/SubLayoutDashboard';

import { ReactComponent as FlagPL } from 'assets/flags/pl.svg';
import { ReactComponent as FlagEN } from 'assets/flags/gb.svg';

import styles from 'theme/components/layouts/MainLayout/components/WiseMenuPopup/WiseMenuPopup.module.scss';

const WiseMenuPopup = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();
  const { profile, overloginUserId } = useSelector((state) => state.auth);
  const { pathname, search } = useLocation();

  const { data: languagesData } = useGetLayoutLanguages();

  const { mutateAsync: refetchUrlResolverToLanguage } = useGetLayoutUrlResolverToLanguage({
    onSuccess: (data) => {
      const params = {
        ...qs.parse(search),
        ...qs.parse(data.parameters)
      };
      navigate(`/${data.url_shortcut}?${qs.stringify(params)}`, { replace: true });
    }
  });

  const { mutateAsync: overLogoutAsync } = usePostOverLogout();

  const flags: Record<string, ReactElement> = {
    en: <FlagEN />,
    pl: <FlagPL />
  };

  const handleChangeLanguage = async (id: string) => {
    if (id === i18n.language) return;

    const getPosition = (string: string, subString: string, index: number) =>
      string.split(subString, index).join(subString).length;
    const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2)).replace('/', '');
    const urlKey = pathname.replace(urlPrefix, '').replaceAll('/', '');

    try {
      await refetchUrlResolverToLanguage({ urlKey, fromLanguage: urlPrefix, toLanguage: id });
      i18n.changeLanguage(id);
    } catch {
      i18n.changeLanguage(id);
    }
  };

  const handleLogout = async () => {
    if (overloginUserId) {
      await overLogoutAsync();
      if (pathname.includes('/cart/')) {
        navigate('/cart/0');
      }
      dispatch(reduxActions.setCurrentCartId(null));
      dispatch(reduxActions.setOverlogin(null));
      dispatch(reduxActions.setIsMobileMenu(false));
      return;
    }
    dispatch(reduxActions.signOut());
    dispatch(reduxActions.setIsMobileMenu(false));
  };

  const isLogged = profile?.role !== 'ROLE_OPEN_PROFILE';

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Layouts-MainLayout-Components-WiseMenuPopup'
      )}>
      <div className={styles.topBox}>
        {isLogged && (
          <>
            <span className={styles.label}>
              <Trans>Zalogowany jako</Trans>:
            </span>
            <span className={styles.name}>
              {profile?.first_name} {profile?.last_name}
            </span>
            <span className={styles.company}>{profile?.customer.name}</span>
          </>
        )}
        <button
          onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
          className={styles.closeButton}
          aria-label={t('Zamknij') || 'close'}>
          <X />
        </button>
      </div>

      <div className={styles.menuBox}>
        {isLogged && profile?.role && (
          <WiseDashboardMenu
            accessRoutes={filterAccessRoutesByPermissions(
              userRolesAccessRoutes()[`${profile.role}`],
              profile.role,
              profile?.additional_information?.company_user_settings
            )}
          />
        )}
      </div>

      <div className={styles.footerBox}>
        <div className={styles.sectionTitle}>
          <Trans>Język</Trans>
        </div>
        <div className={styles.languageList}>
          {languagesData?.items.map((lang) => (
            <button
              key={lang.id}
              type="button"
              className={classnames(styles.languageItem, {
                [styles.languageItemActive]: lang.id === i18n.language
              })}
              onClick={() => handleChangeLanguage(lang.id)}>
              {flags[lang.id]}
              <span>{lang.name}</span>
            </button>
          ))}
        </div>

        {isLogged && (
          <button type="button" className={styles.logoutButton} onClick={handleLogout}>
            <BoxArrowRight />
            <span>{overloginUserId ? t('Wróć na konto') : t('Wyloguj')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default WiseMenuPopup;
